import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { videoService } from '../../services'
import { useAuthStore, useUploadStore } from '../../stores'
import type { VideoVisibility } from '../../types/database.types'
import { Button, Input, Spinner } from '../../components/common'
import { useToast } from '../../components/common/Toast'

const CATEGORIES = [
  'Strength',
  'Mobility',
  'Cardio',
  'Stretching',
  'Balance',
  'Rehabilitation',
  'Warm-up',
  'Cool-down',
  'Core',
  'Upper Body',
  'Lower Body',
  'Full Body',
]

export function VideoUploadPage() {
  const navigate = useNavigate()
  const { canUploadVideos } = useAuthStore()
  const { startUpload, updateProgress, setFinalizing, completeUpload, setUploadError, hasActiveUploads } = useUploadStore()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [tags, setTags] = useState('')
  const [visibility, setVisibility] = useState<VideoVisibility>('public')
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Warn user if they try to close/refresh during active uploads
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads()) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasActiveUploads])

  if (!canUploadVideos()) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Video Upload Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Video uploads are available for Advanced tier subscribers and above.
          Upgrade your subscription to start uploading your own exercise videos.
        </p>
        <Button onClick={() => navigate('/dashboard/subscription')}>
          Upgrade Subscription
        </Button>
      </div>
    )
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    // Validate file type
    if (!selectedFile.type.startsWith('video/')) {
      toast.error('Please select a video file')
      return
    }

    // Validate file size (500MB max)
    const maxSize = 500 * 1024 * 1024
    if (selectedFile.size > maxSize) {
      toast.error('Video file must be smaller than 500MB')
      return
    }

    setFile(selectedFile)
  }

  const validateAndSetFile = (selectedFile: File) => {
    // Validate file type
    if (!selectedFile.type.startsWith('video/')) {
      toast.error('Please select a video file')
      return
    }

    // Validate file size (500MB max)
    const maxSize = 500 * 1024 * 1024
    if (selectedFile.size > maxSize) {
      toast.error('Video file must be smaller than 500MB')
      return
    }

    setFile(selectedFile)
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      validateAndSetFile(droppedFile)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!file) {
      toast.error('Please select a video file')
      return
    }

    if (!title.trim()) {
      toast.error('Please enter a video title')
      return
    }

    setIsSubmitting(true)

    try {
      // Step 1: Get upload URL from our API (creates video record with status='uploading')
      const uploadData = await videoService.createUploadUrl(
        title.trim(),
        description.trim() || undefined,
        visibility
      )

      if (!uploadData) {
        throw new Error('Failed to create upload URL')
      }

      // Step 2: Create XHR for direct Mux upload
      const xhr = new XMLHttpRequest()

      // Parse tags
      const parsedTags = tags
        ? tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : null

      // Register upload in store (so My Videos page can track it)
      startUpload(
        uploadData.videoId,
        xhr,
        file.name,
        title.trim(),
        category || null,
        parsedTags
      )

      // Set up progress handler
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          updateProgress(uploadData.videoId, percent)
        }
      })

      // Set up completion handler (runs in background after navigation)
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setFinalizing(uploadData.videoId)
          await completeUpload(uploadData.videoId)
        } else {
          setUploadError(uploadData.videoId, `Upload failed: ${xhr.status}`)
        }
      }

      xhr.onerror = () => {
        setUploadError(uploadData.videoId, 'Upload failed - network error')
      }

      // Start the upload
      xhr.open('PUT', uploadData.uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)

      // Navigate immediately - upload continues in background
      toast.success('Upload started! Once ready, you can set a custom thumbnail from Edit.')
      navigate('/dashboard/videos')
    } catch (error) {
      console.error('Upload failed:', error)
      const message = error instanceof Error ? error.message : 'Failed to start upload. Please try again.'
      toast.error(message)
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Upload Video</h1>
        <p className="mt-1 text-gray-600">
          Share your exercise knowledge with the world.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Video File
          </label>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              file
                ? 'border-blue-300 bg-blue-50'
                : isDragging
                  ? 'border-blue-500 bg-blue-100'
                  : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            {file ? (
              <div>
                <svg
                  className="w-10 h-10 text-blue-600 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setFile(null)
                  }}
                  className="text-sm text-blue-600 hover:underline mt-2"
                >
                  Choose a different file
                </button>
              </div>
            ) : (
              <div>
                <svg
                  className="w-10 h-10 text-gray-400 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="font-medium text-gray-900">
                  Drag and drop your video here, or click to browse
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  MP4, MOV, or WebM up to 500MB
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Title */}
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Single Leg Squat for Knee Rehab"
          required
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the exercise, target muscles, and any important form cues..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a category</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <Input
          label="Tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="knee, rehab, strength (comma separated)"
          helperText="Add tags to help others find your video"
        />

        {/* Visibility */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Visibility
          </label>
          <div className="space-y-2">
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === 'public'}
                onChange={(e) =>
                  setVisibility(e.target.value as VideoVisibility)
                }
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Public</p>
                <p className="text-sm text-gray-500">
                  Anyone can find and watch this video
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="visibility"
                value="organization"
                checked={visibility === 'organization'}
                onChange={(e) =>
                  setVisibility(e.target.value as VideoVisibility)
                }
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Organization Only</p>
                <p className="text-sm text-gray-500">
                  Only members of your organization can view
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === 'private'}
                onChange={(e) =>
                  setVisibility(e.target.value as VideoVisibility)
                }
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Private</p>
                <p className="text-sm text-gray-500">
                  Only you can view this video
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/videos')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || !file}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" />
                <span className="ml-2">Starting upload...</span>
              </>
            ) : (
              'Upload Video'
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
